import { StdImage } from './StdImage';
import { Address } from './Address';
import {
  types,
  Instance,
  SnapshotOut,
  SnapshotIn,
  getRoot,
  flow,
} from 'mobx-state-tree';
import { modelWithCRUD } from './utils/modelWithCRUD';
import { RootModelType } from './Root';
import { SetupIntent } from '@stripe/stripe-js';

export type IDashboardGoal = {
  title: string;
  key: string;
  type: 'goal';
  value: { goal: number; current: number };
};
export type IDashboardLeaderboardMonth = {
  value: {
    member: string;
    revenue: number;
    contribution_margin: number;
    goal: number;
  }[];
  date: string;
};
export type IDashboardLeaderboard = {
  title: string;
  key: string;
  type: 'leaderboard';
  value: IDashboardLeaderboardMonth[];
};
export type IDashboardGraph = {
  title: string;
  key: string;
  type: 'graph';
  value: {
    date: string;
    value: number;
  }[];
};

export type IDashboard = (
  | IDashboardGoal
  | IDashboardLeaderboard
  | IDashboardGraph
)[];

export type IBilling = {
  id: string | number;
  created_at: string;
  stripe_id: string;
  card: {
    brand: string;
    exp_month: string;
    exp_year: string;
    country: string;
    last4: string;
  };
  plans: {
    id: string;
    active: boolean;
    amount: number;
    currency: string;
    interval: 'day' | 'week' | 'month' | 'year';
    product: string;
    nickname: string;
  }[];

  current_subscription?: {
    id: string | number;
    plan: string;
    quantity: number;
    start: string;
    status: string;
    cancel_at_period_end?: string | null;
    canceled_at?: string | null;
    current_period_end?: string | null;
    current_period_start?: string | null;
    ended_at?: string | null;
    trial_end?: string | null;
    trial_start?: string | null;
    amount: number;
    amount_currency: string;

    active: boolean;
    trialing: boolean;
    has_payment_method: boolean;
  };
  invoices: {
    id: string;
    number?: string | null;
    stripe_id: string;
    created_at: string;
    status: 'open' | 'draft' | 'paid' | 'uncollectible' | 'void';
    pdf_url: string;
    total: number;
    total_currency: string;
  }[];
};

export const Organisation = types.model('Organisation', {
  id: types.union(types.identifier, types.identifierNumber),
  name: types.string,
  owner: types.maybeNull(types.union(types.string, types.number)),

  logo: types.maybeNull(StdImage),
  icon: types.maybeNull(StdImage),
  terms_pdf: types.maybeNull(types.string),

  legal_name: types.maybeNull(types.string),
  vat_number: types.maybeNull(types.string),
  address: types.maybeNull(Address),
  email: types.maybeNull(types.string),
  phone: types.maybeNull(types.string),

  next_offer_number: types.number,
  next_order_number: types.number,
  next_invoice_number: types.number,
  next_company_number: types.number,

  default_currency: types.optional(types.string, 'DKK'),

  brand_color: types.maybeNull(types.string),
  logo_placement: types.maybeNull(types.string),
  logo_size: types.maybeNull(types.string),

  offer_note: types.maybeNull(types.string),
  order_note: types.maybeNull(types.string),
  invoice_note: types.maybeNull(types.string),

  subscription: types.maybeNull(
    types.frozen<IBilling['current_subscription']>()
  ),

  show_quick_start_guide: types.boolean,
  members_count: types.number,
});

export const OrganisationStore = modelWithCRUD(
  'organisations',
  Organisation,
  false // Force the organisation store to not use organisational permission styling, e.g. /organistaions/{org_pk}/model_name/{id}/
)
  .named('OrganisationStore')
  .actions((self) => ({
    updateLogoAndIcon: flow(function* updateLogoAndIcon(
      organisation: IOrganisation,
      logo?: File,
      icon?: File
    ) {
      const formData = new FormData();
      if (logo) formData.append('logo', logo);
      if (icon) formData.append('icon', icon);
      const res = yield self.env.api.post(
        `${self.apiPath}/${organisation.id}/logo/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return self.itemMap.put(res.data);
    }),
    updateTermsPDF: flow(function* updateTermsPDF(
      organisation: IOrganisation,
      pdf: File
    ) {
      const formData = new FormData();
      formData.append('terms_pdf', pdf);

      const res = yield self.env.api.post(
        `${self.apiPath}/${organisation.id}/add-terms/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return self.itemMap.put(res.data);
    }),
    getDashboard: flow(function* getDashboard(organisation: IOrganisation) {
      const { data } = yield self.env.api.get<IDashboard>(
        `${self.apiPath}/${organisation.id}/dashboard/`
      );
      return data;
    }),
    getSetupIntent: flow(function* getSetupIntent(
      organisation: IOrganisation,
      setupIntentId?: string | null
    ) {
      const { data } = yield self.env.api.get<SetupIntent>(
        `${self.apiPath}/${organisation.id}/setup-intent/${
          setupIntentId
            ? `?setup_intent_id=${encodeURIComponent(setupIntentId)}`
            : ''
        }`
      );
      if (setupIntentId) {
        yield self.getItem(organisation.id, true);
      }
      return data as SetupIntent;
    }),
    getBilling: flow(function* getBilling(organisation: IOrganisation) {
      const { data } = yield self.env.api.get<IBilling>(
        `${self.apiPath}/${organisation.id}/billing/`
      );
      return data as IBilling;
    }),
    subscribe: flow(function* subscribe(organisation: IOrganisation) {
      const { data } = yield self.env.api.post(
        `${self.apiPath}/${organisation.id}/subscription/`,
        {}
      );
      self.invalidateCaches();
      yield self.getItem(organisation.id, true);
      return data;
    }),
    cancelSubscription: flow(function* cancelSubscription(
      organisation: IOrganisation
    ) {
      const { data } = yield self.env.api.delete(
        `${self.apiPath}/${organisation.id}/subscription/`
      );
      self.invalidateCaches();
      yield self.getItem(organisation.id, true);
      return data;
    }),
  }));

export interface IOrganisation extends Instance<typeof Organisation> {}
export interface IOrganisationSnapshotIn
  extends SnapshotIn<typeof Organisation> {}
export interface IOrganisationSnapshotOut
  extends SnapshotOut<typeof Organisation> {}

export const OrganisationReference = types.safeReference(Organisation, {
  get(identifier, parent): any {
    return (getRoot(parent!) as RootModelType).organisationStore.getItemNaive(
      identifier
    );
  },
  set(value) {
    return value.id;
  },
});
